import * as turf from "@turf/turf";
import type { PoiKind } from "../types";
import type { RouteContext } from "../routing";
import type { ParsedPoi } from "./types";
import { distanceAlongRoute } from "../geo";

export function detectKind(tags: Record<string, string>): PoiKind {
  const name  = tags.name ?? "";
  const brand = tags.brand ?? "";
  const op    = tags.operator ?? "";
  if (/^[Mm]c[Dd]onald/.test(name)) return "mcd";
  if (tags.amenity === "fuel" && (
    /^[Oo]rlen/.test(name) || /^[Oo]rlen/.test(brand) || /^[Oo]rlen/.test(op)
  )) return "orlen";
  if (tags.amenity === "toilets") return "toilet";
  return "other";
}

export function parsePoiResults(
  data:     { elements?: any[] },
  routeCtx: RouteContext,
  polygon:  GeoJSON.Feature,
): ParsedPoi[] {
  const pt = turf.polygon((polygon.geometry as GeoJSON.Polygon).coordinates);
  const pois: ParsedPoi[] = [];
  const seen = new Set<string>();

  for (const el of data.elements ?? []) {
    // Skip elements with no useful tags (e.g. bare way-member nodes)
    if (!el.tags || Object.keys(el.tags).length === 0) continue;

    // Nodes have lat/lon directly; ways have a "center" object
    const lat = el.lat ?? el.center?.lat;
    const lng = el.lon ?? el.center?.lon;
    if (lat === undefined || lng === undefined) continue;

    // Deduplicate by rounded coordinates
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const tags = el.tags;
    const kind = detectKind(tags);

    // orlen tag applies only to actual fuel stations
    if (kind === "orlen" && tags.amenity !== "fuel") continue;

    const name = tags.name ?? tags.brand ?? tags.operator
      ?? (tags.amenity === "fuel"   ? "Stacja benzynowa"
        : tags.amenity === "toilets" ? "Toaleta publiczna"
        : "?");
    const amenity = tags.amenity ?? "";

    const point = turf.point([lng, lat]);
    if (!turf.booleanPointInPolygon(point, pt)) continue;

    const routeDistM = distanceAlongRoute({ lat, lng }, routeCtx.geometry);

    pois.push({ id: String(el.id), osmType: el.type === "way" ? "way" : "node", lat, lng, name, amenity, tags, kind, routeDistM });
  }

  return pois;
}
